import React, { useState } from 'react';
import { string, bool } from 'prop-types';
import { Play, Pause } from '@one-thd/sui-icons';
import classNames from 'classnames/bind';
import styles from './video-hero-image.scss';

const VideoHeroImage = ({
  imageUrl = '',
  altText = '',
  fullWidth = false,
  brandHero = false,
  videoClasses = '',
  isBentoHero = false,
  hrefLink = '',
  desktopVideoUrl = '',
  mobileVideoUrl = ''
}) => {
  const [videoPaused, setVideoPaused] = useState(false);

  const handlePlayPause = (event) => {
    event.stopPropagation();
    const videoElement = event.currentTarget.closest('.video-hero-image')?.querySelector('video');

    if (!videoElement) return;

    setVideoPaused((prev) => {
      if (prev) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
      return !prev;
    });
  };

  const handleVideoClick = () => {
    if (isBentoHero && hrefLink) {
      window.location.href = hrefLink;
    }
  };

  const videoButtonDefaultClasses = classNames(
    'sui-justify-center sui-items-center sui-bg-inactive sui-rounded-full sui-h-8 sui-w-8',
  );

  const playButtonClasses = classNames(
    'play-button',
    videoButtonDefaultClasses,
    { 'sui-flex': videoPaused, 'sui-hidden': !videoPaused }
  );

  const pauseButtonClasses = classNames(
    'pause-button',
    videoButtonDefaultClasses,
    { 'sui-flex': !videoPaused, 'sui-hidden': videoPaused }
  );

  const videoButton = (
    <button
      type="button"
      className="hero-video-button sui-absolute sui-bottom-4 sui-right-4 sui-flex sui-justify-end sui-items-end"
      aria-label="pause or play video"
      onClick={handlePlayPause}
      onKeyUp={(event) => event.stopPropagation()}
    >
      <div
        data-testid="play-button"
        className={playButtonClasses}
      >
        <div className="play-button-hover sui-pl-1"><Play color="brand" size="small" /></div>
        <div className="play-button-no-hover sui-pl-1"><Play color="primary" size="small" /></div>
      </div>
      <div
        className={pauseButtonClasses}
      >
        <div className="pause-button-hover"><Pause color="brand" size="small" /></div>
        <div className="pause-button-no-hover"><Pause color="primary" size="small" /></div>
      </div>
    </button>
  );

  const containerClasses = classNames(
    'video-hero-image sui-overflow-hidden sui-relative',
    {
      'sui-w-full': fullWidth,
      'sui-h-full': isBentoHero,
      'sui-row-start-1 md:sui-row-start-auto': brandHero,
    }
  );

  return (
    <div
      className={containerClasses}
      onClick={handleVideoClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === ' ') {
          event.preventDefault();
          handleVideoClick();
        }
      }}
    >
      {isBentoHero ? (
        <video
          className={videoClasses}
          aria-label={altText}
          autoPlay
          playsInline
          muted
          loop
          style={{ width: '100%' }}
          controls=""
        >
          <source
            className="sui-w-full sui-top-0 sui-left-0"
            src={desktopVideoUrl}
            type="video/mp4"
            media="(min-width: 1024px)"
          />
          <source
            className="sui-w-full sui-top-0 sui-left-0"
            src={mobileVideoUrl}
            type="video/mp4"
          />
        </video>
      ) : (
        <video
          className={videoClasses}
          aria-label={altText}
          autoPlay
          playsInline
          muted
          loop
          controls=""
        >
          <source src={imageUrl} type="video/mp4" />
        </video>
      )}
      {videoButton}
    </div>
  );
};

VideoHeroImage.propTypes = {
  imageUrl: string,
  altText: string,
  fullWidth: bool,
  brandHero: bool,
  videoClasses: string,
  isBentoHero: bool,
  hrefLink: string,
  desktopVideoUrl: string,
  mobileVideoUrl: string
};

export { VideoHeroImage };
